import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ENTITLEMENT_FEATURES, type PackageConfig, type PackageDetailView, type PackageStatus, type PackageSummaryView } from '@lp/contracts';
import { Plus } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { errorText, fieldProblems, get, post } from '@/lib/api';
import { useIsSuperAdmin } from '@/lib/auth';
import { useT } from '@/lib/i18n';
import { featuresOn, LimitValue, ProductsAllowed } from '@/components/package-config';
import { PriceText } from '@/components/edit-dialogs';
import { Badge, Button, Dialog, Empty, ErrorBox, Field, Input, Loading, PageHeader, Select } from '@/components/ui';

export const usePackages = () => useQuery({ queryKey: ['packages'], queryFn: () => get<PackageSummaryView[]>('/platform/packages') });

export function PackageStatusBadge({ status }: { status: PackageStatus }) {
  const t = useT();
  return <Badge tone={status === 'active' ? 'green' : 'gray'}>{t(status === 'active' ? 'Active' : 'Archived')}</Badge>;
}

/** the packages (Phase 5, docs/51): Package = Features + Limits + Products. One row per package. */
export function PackagesPage() {
  const t = useT();
  const nav = useNavigate();
  const superAdmin = useIsSuperAdmin();
  const list = usePackages();
  const [creating, setCreating] = useState(false);
  return (
    <>
      <PageHeader
        title={t('Packages')}
        subtitle={t('What a client may have and use. The server checks it on every sale and install; a client stays on its package version until it is moved.')}
        actions={
          superAdmin && (
            <Button icon={<Plus size={14} />} onClick={() => setCreating(true)}>
              {t('New package')}
            </Button>
          )
        }
      />
      <div className="rounded-lg border border-ink-200 bg-white">
        {list.isLoading ? (
          <div className="p-4">
            <Loading />
          </div>
        ) : list.error ? (
          <div className="p-4">
            <ErrorBox text={errorText(list.error)} />
          </div>
        ) : !list.data?.length ? (
          <Empty title={t('No package yet')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink-100 text-left text-xs font-medium text-ink-500">
                  <th className="px-4 py-2">{t('Package')}</th>
                  <th className="px-4 py-2">{t('Price')}</th>
                  <th className="px-4 py-2">{t('Products')}</th>
                  <th className="px-4 py-2 text-right">{t('Agents')}</th>
                  <th className="px-4 py-2 text-right">{t('Console users')}</th>
                  <th className="px-4 py-2 text-right">{t('Features')}</th>
                  <th className="px-4 py-2">{t('Version')}</th>
                  <th className="px-4 py-2 text-right">{t('Clients')}</th>
                  <th className="px-4 py-2">{t('State')}</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((p) => (
                  <tr key={p.code} onClick={() => nav(`/packages/${p.code}`)} className="cursor-pointer border-b border-ink-100 last:border-0 hover:bg-ink-50" data-testid={`package-row-${p.code}`}>
                    <td className="px-4 py-2.5">
                      <span className="font-medium text-ink-900">{p.nameEn}</span> <span className="font-mono text-[11px] text-ink-500">{p.code}</span>
                      <span className="block text-xs text-ink-500">{p.nameKm}</span>
                    </td>
                    <td className="px-4 py-2.5 text-xs" data-testid={`package-price-${p.code}`}>
                      <PriceText price={p.price} />
                    </td>
                    <td className="px-4 py-2.5 text-xs">
                      {p.config ? (
                        <>
                          <ProductsAllowed products={p.config.products} />
                          <span className="mt-0.5 block text-ink-500">
                            {t('up to')} <LimitValue limit={p.config.limits.products} />
                          </span>
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{p.config ? <LimitValue limit={p.config.limits.agents} /> : '—'}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{p.config ? <LimitValue limit={p.config.limits.console_users} /> : '—'}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{p.config ? `${featuresOn(p.config)} / ${ENTITLEMENT_FEATURES.length}` : '—'}</td>
                    <td className="px-4 py-2.5 text-xs">
                      {p.publishedVersion ? `v${p.publishedVersion}` : '—'}
                      {p.draftVersion && (
                        <span className="ml-1.5">
                          <Badge tone="blue">
                            {t('Draft')} v{p.draftVersion}
                          </Badge>
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{p.clients}</td>
                    <td className="px-4 py-2.5">
                      <PackageStatusBadge status={p.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {creating && list.data && <NewPackage packages={list.data} onClose={() => setCreating(false)} />}
    </>
  );
}

/** a new package starts as a copy of another one's content, as a draft — the editor follows */
function NewPackage({ packages, onClose }: { packages: PackageSummaryView[]; onClose: () => void }) {
  const t = useT();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [nameKm, setNameKm] = useState('');
  const [description, setDescription] = useState('');
  const copyable = packages.filter((p) => p.config);
  const [from, setFrom] = useState(copyable[0]?.code ?? '');
  const create = useMutation({
    mutationFn: () => {
      const config: PackageConfig = copyable.find((p) => p.code === from)?.config ?? { features: Object.fromEntries(ENTITLEMENT_FEATURES.map((f) => [f, false])) as PackageConfig['features'], limits: { products: 0, agents: 0, console_users: 0, domains: 0 }, products: [], settings: {} };
      return post<PackageDetailView>('/platform/packages', { code, nameEn, nameKm, ...(description.trim() ? { description } : {}), config });
    },
    onSuccess: (p) => {
      void qc.invalidateQueries({ queryKey: ['packages'] });
      nav(`/packages/${p.code}/versions/1/edit`);
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };
  const problems = fieldProblems(create.error);
  return (
    <Dialog
      title={t('New package')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button type="submit" form="new-package" disabled={!code || !nameEn || !nameKm || create.isPending}>
            {t('Create draft')}
          </Button>
        </>
      }
    >
      <form id="new-package" onSubmit={submit} className="space-y-3">
        <Field label={t('Code')}>
          <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={12} placeholder="GOLD" />
        </Field>
        <Field label={t('Name (English)')}>
          <Input value={nameEn} onChange={(e) => setNameEn(e.target.value)} maxLength={80} />
        </Field>
        <Field label={t('Name (Khmer)')}>
          <Input value={nameKm} onChange={(e) => setNameKm(e.target.value)} maxLength={80} />
        </Field>
        <Field label={t('Description')}>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={400} />
        </Field>
        <Field label={t('Start as a copy of')}>
          <Select value={from} onChange={(e) => setFrom(e.target.value)} className="w-full">
            {copyable.map((p) => (
              <option key={p.code} value={p.code}>
                {p.nameEn} ({p.code})
              </option>
            ))}
          </Select>
        </Field>
        {create.error && <ErrorBox text={[errorText(create.error), ...problems].join(' — ')} />}
      </form>
    </Dialog>
  );
}
